/**
 * Bet bag for FishPrawnCrab — bet items keyed by numeric betItemId.
 * Mirrors the `_singleBets` Map<int,int> in game_screen.dart:56.
 */

export interface BetEntry {
  betItemId: number;
  amount: number;
  chips: number[];
}

export class BetCollection {
  private entries: Map<number, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { betItemId: v.betItemId, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(betItemId: number, amount: number): void {
    const cur = this.entries.get(betItemId);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(betItemId, { betItemId, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(betItemId: number): void {
    this.entries.delete(betItemId);
  }

  amountFor(betItemId: number): number {
    return this.entries.get(betItemId)?.amount ?? 0;
  }

  chipsFor(betItemId: number): readonly number[] {
    return this.entries.get(betItemId)?.chips ?? [];
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  /** Iterable of [betItemId, amount] for the placed bets. */
  *placedBets(): IterableIterator<[number, number]> {
    for (const v of this.entries.values()) {
      if (v.amount > 0) yield [v.betItemId, v.amount];
    }
  }

  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: v.betItemId, amount: v.amount });
    }
    return out;
  }

  /**
   * One { betItemId, amount } per INDIVIDUAL chip placed — NOT collapsed to a
   * per-item total like toBetLines(). Used for multiplayer submission: the hub
   * validates each PlaceBet amount against the configured chip denominations
   * and rejects a summed total (e.g. 300) with "Invalid Chip Amount", so the
   * batched submit must replay each chip (e.g. 100, 100, 100) for the server to
   * accumulate. Order within a bet item is preserved (the push order in add()).
   */
  toChipLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      for (const chip of v.chips) {
        if (chip > 0) out.push({ betItemId: v.betItemId, amount: chip });
      }
    }
    return out;
  }
}
